<template>
  <div class="container-fluid p-4">
    <div class="box mt-3">
      <div class="page-header">
        <h2>{{ $t('masterEvents.title') }} > {{ isEdit ? $t('masterEvents.edit') : $t('masterEvents.create') }}</h2>
      </div>

      <form @submit.prevent="save" novalidate>
        <!-- Image Upload Section (New mode only) -->
        <div v-if="!isEdit" class="image-upload-section mb-4">
          <!-- Uploaded Images Preview (Horizontal Scroll) -->
          <div v-if="uploadedImages.length > 0" class="images-preview-container">
            <div class="images-preview-scroll">
              <div v-for="(img, index) in uploadedImages" :key="index" class="image-preview-item">
                <img
                  :src="getImageSrc(img)"
                  :alt="'Image ' + (index + 1)"
                  class="preview-image clickable"
                  @click="openImagePreview(img)"
                />
                <button type="button" class="btn-remove-image" @click="removeImage(index)">
                  <i class="bi bi-x-circle-fill"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Add Image Button -->
          <div class="add-image-section">
            <input
              type="file"
              ref="imageInput"
              @change="handleImageUpload"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              multiple
              class="d-none"
            />
            <button
              type="button"
              class="btn btn-add-image"
              @click="triggerImageInput"
              :disabled="uploadedImages.length >= 5 || analyzing"
            >
              <i class="bi bi-plus"></i> {{ $t('masterEvents.addImage') }}
            </button>
            <span class="image-count-hint">{{ $t('masterEvents.imageCountHint', { count: uploadedImages.length, max: 5 }) }}</span>
          </div>

          <!-- Analyze Button -->
          <div v-if="uploadedImages.length > 0" class="analyze-section mt-3">
            <button
              type="button"
              class="btn btn-analyze"
              @click="analyzeImages"
              :disabled="analyzing"
            >
              <span v-if="analyzing" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-magic"></i>
              {{ analyzing ? $t('masterEvents.analyzing') : $t('masterEvents.analyzeWithAI') }}
            </button>
          </div>
        </div>

        <!-- Event Forms -->
        <div v-for="(eventItem, eventIndex) in events" :key="eventIndex" class="event-accordion mb-3">
          <div
            class="event-accordion-header"
            @click="toggleEventAccordion(eventIndex)"
          >
            <h5 class="mb-0">
              <span class="event-title-text">{{ $t('masterEvents.eventNumber', { number: eventIndex + 1 }) }}</span>
            </h5>
            <i :class="eventItem.expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="accordion-icon"></i>
          </div>

          <div v-show="eventItem.expanded" class="event-accordion-body">
            <!-- Event Title -->
            <div class="mb-3">
              <label class="form-label">{{ $t('masterEvents.eventName') }} <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': getEventError(eventIndex, 'title') }"
                v-model="eventItem.title"
                maxlength="255"
                required
              />
              <div v-if="getEventError(eventIndex, 'title')" class="invalid-feedback">
                {{ getEventError(eventIndex, 'title') }}
              </div>
            </div>

            <!-- Organizers Section -->
            <div class="mb-3 organizers-section">
              <div class="section-header">
                <label class="form-label mb-0">{{ $t('masterEvents.organizers') }} ({{ eventItem.organizers.length }}) <span class="text-danger">*</span></label>
              </div>
              <div class="section-content">
                <div v-for="(organizer, orgIndex) in eventItem.organizers" :key="orgIndex" class="organizer-row mb-2">
                  <div class="organizer-select-container">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': getOrganizerError(eventIndex, orgIndex) }"
                      v-model="organizer.searchText"
                      @input="handleOrganizerSearchInRow(eventIndex, orgIndex)"
                      @focus="organizer.showDropdown = true"
                      :placeholder="$t('masterEvents.selectOrganizer')"
                    />
                    <div v-if="organizer.showDropdown && getFilteredOrganizersForRow(eventIndex, orgIndex).length > 0" class="organizer-dropdown">
                      <div
                        v-for="org in getFilteredOrganizersForRow(eventIndex, orgIndex)"
                        :key="org.id"
                        class="organizer-dropdown-item"
                        @click="selectOrganizerInRow(eventIndex, orgIndex, org)"
                      >
                        {{ org.name }}
                      </div>
                    </div>
                  </div>
                  <div class="form-check form-switch organizer-primary-switch">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      role="switch"
                      :id="'isPrimary-' + eventIndex + '-' + orgIndex"
                      v-model="organizer.is_primary"
                    />
                    <label class="form-check-label" :for="'isPrimary-' + eventIndex + '-' + orgIndex">
                      {{ $t('masterEvents.isPrimary') }}
                    </label>
                  </div>
                  <button type="button" class="btn btn-link btn-remove-organizer" @click="removeOrganizerFromEvent(eventIndex, orgIndex)">
                    <i class="bi bi-x-circle"></i>
                  </button>
                </div>
                <div v-if="getEventError(eventIndex, 'organizers')" class="text-danger small mb-2">
                  {{ getEventError(eventIndex, 'organizers') }}
                </div>
                <button type="button" class="btn btn-primary btn-sm btn-add-row" @click="addOrganizerToEvent(eventIndex)">
                  <i class="bi bi-plus"></i> {{ $t('masterEvents.addOrganizer') }}
                </button>
              </div>
            </div>

            <!-- Description -->
            <div class="mb-3">
              <label class="form-label">{{ $t('masterEvents.description') }}</label>
              <textarea
                class="form-control"
                v-model="eventItem.description"
                rows="4"
              ></textarea>
            </div>

            <!-- Date Range -->
            <div class="mb-3">
              <label class="form-label">{{ $t('masterEvents.dateRange') }} <span class="text-danger">*</span></label>
              <div class="date-range-row">
                <input
                  type="date"
                  class="form-control date-input"
                  :class="{ 'is-invalid': getEventError(eventIndex, 'start_date') }"
                  v-model="eventItem.startDate"
                  required
                />
                <span class="date-separator">~</span>
                <input
                  type="date"
                  class="form-control date-input"
                  :class="{ 'is-invalid': getEventError(eventIndex, 'end_date') }"
                  v-model="eventItem.endDate"
                  required
                />
              </div>
              <div v-if="getEventError(eventIndex, 'start_date')" class="text-danger small mt-1">
                {{ getEventError(eventIndex, 'start_date') }}
              </div>
              <div v-if="getEventError(eventIndex, 'end_date')" class="text-danger small mt-1">
                {{ getEventError(eventIndex, 'end_date') }}
              </div>
            </div>

            <!-- Fees Section -->
            <div class="mb-3 fees-section">
              <div class="section-header">
                <label class="form-label mb-0">{{ $t('masterEvents.fees') }} ({{ eventItem.fees.length }})</label>
              </div>
              <div class="section-content">
                <div v-for="(fee, feeIndex) in eventItem.fees" :key="feeIndex" class="fee-row-wrapper mb-2">
                  <div class="fee-row">
                    <div class="fee-row-inputs">
                      <input
                        type="text"
                        class="form-control fee-type-input"
                        :class="{ 'is-invalid': getFeeError(eventIndex, feeIndex, 'fee_type') }"
                        v-model="fee.fee_type"
                        :placeholder="$t('masterEvents.feeType')"
                      />
                      <div class="input-group fee-amount-group">
                        <span class="input-group-text">¥</span>
                        <input
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': getFeeError(eventIndex, feeIndex, 'amount') }"
                          v-model.number="fee.amount"
                          :placeholder="$t('masterEvents.feeAmount')"
                          min="0"
                        />
                      </div>
                      <input
                        type="text"
                        class="form-control fee-description-input"
                        v-model="fee.description"
                        :placeholder="$t('masterEvents.feeDescription')"
                      />
                    </div>
                    <button type="button" class="btn btn-link btn-remove-fee" @click="removeFeeFromEvent(eventIndex, feeIndex)">
                      <i class="bi bi-x-circle"></i>
                    </button>
                  </div>
                  <div v-if="getFeeError(eventIndex, feeIndex, 'fee_type') || getFeeError(eventIndex, feeIndex, 'amount')" class="fee-errors">
                    <div v-if="getFeeError(eventIndex, feeIndex, 'fee_type')" class="text-danger small">
                      {{ getFeeError(eventIndex, feeIndex, 'fee_type') }}
                    </div>
                    <div v-if="getFeeError(eventIndex, feeIndex, 'amount')" class="text-danger small">
                      {{ getFeeError(eventIndex, feeIndex, 'amount') }}
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-primary btn-sm btn-add-row" @click="addFeeToEvent(eventIndex)">
                  <i class="bi bi-plus"></i> {{ $t('masterEvents.addFee') }}
                </button>
              </div>
            </div>

            <!-- Capacity -->
            <div class="mb-3">
              <label class="form-label">{{ $t('masterEvents.capacityWithHint') }}</label>
              <input
                type="number"
                class="form-control capacity-input"
                v-model.number="eventItem.capacity"
                min="0"
              />
            </div>

            <!-- Forecasted Visitor Count -->
            <div class="mb-3">
              <label class="form-label">{{ $t('masterEvents.forecastedVisitorCount') }}</label>
              <input
                type="number"
                class="form-control capacity-input"
                :class="{ 'is-invalid': getEventError(eventIndex, 'forecasted_visitor_count') }"
                v-model.number="eventItem.forecastedVisitorCount"
                min="1"
              />
              <div v-if="getEventError(eventIndex, 'forecasted_visitor_count')" class="invalid-feedback">
                {{ getEventError(eventIndex, 'forecasted_visitor_count') }}
              </div>
            </div>

            <!-- Is Online -->
            <div class="mb-3">
              <div class="form-check form-switch">
                <input
                  type="checkbox"
                  class="form-check-input"
                  role="switch"
                  :id="'isOnline-' + eventIndex"
                  v-model="eventItem.isOnline"
                />
                <label class="form-check-label" :for="'isOnline-' + eventIndex">
                  {{ $t('masterEvents.onlineEvent') }}
                </label>
              </div>
            </div>

            <!-- Delete Event Button (only if more than 1 event in new mode) -->
            <div v-if="!isEdit && events.length > 1" class="delete-event-section">
              <button type="button" class="btn btn-link btn-delete-event" @click="removeEvent(eventIndex)">
                <i class="bi bi-x"></i> {{ $t('masterEvents.deleteEvent') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Add Event Button (New mode only, max 5 events) -->
        <div v-if="!isEdit && events.length < 5" class="add-event-section" @click="addEvent">
          <span class="add-event-text">{{ $t('masterEvents.addEventInput') }}</span>
          <i class="bi bi-plus add-event-icon"></i>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons mt-4">
          <button type="submit" class="btn btn-primary btn-submit" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEdit ? $t('masterEvents.update') : $t('masterEvents.createButton') }}
          </button>

          <button type="button" class="btn btn-cancel" @click="cancel">
            {{ $t('masterEvents.cancel') }}
          </button>

          <button
            v-if="isEdit"
            type="button"
            class="btn btn-link btn-delete"
            @click="confirmDelete"
          >
            <i class="bi bi-x-circle"></i> {{ $t('masterEvents.delete') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Error Dialog -->
    <ErrorDialog
      v-model="showErrorDialog"
      :message="errorDialogMessage"
    />

    <!-- Confirm Dialog -->
    <ConfirmDialog
      v-model="showConfirmDialog"
      :message="t('masterEvents.confirmDelete')"
      @confirm="handleDeleteConfirm"
    />

    <!-- Image Preview Dialog -->
    <div v-if="showImagePreviewDialog" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-dialog" @click.stop>
        <button type="button" class="btn-close-preview" @click="closeImagePreview">
          <i class="bi bi-x-lg"></i>
        </button>
        <img :src="previewImageSrc" alt="Preview" class="preview-large-image" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { colors } from '../../../../../consts/colors';
import {
  getMasterEvent,
  updateMasterEvent,
  deleteMasterEvent,
  checkMasterEventDeletable,
  getAllEventOrganizers,
  createMasterEventsBulk,
  analyzeEventFlyersMultiple
} from '../../../../api/user/EventApi';
import useFlashMessage from '../../../../composables/useFlashMessage';
import ConfirmDialog from '../../../../components/common/ConfirmDialog.vue';
import ErrorDialog from '../../../../components/common/ErrorDialog.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { successMessage, errorMessage } = useFlashMessage();

// Create empty organizer object
const createEmptyOrganizer = () => ({
  store_id: '',
  is_primary: false,
  searchText: '',
  showDropdown: false
});

// Create empty event object
const createEmptyEvent = () => ({
  id: null,
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  capacity: null,
  forecastedVisitorCount: null,
  isOnline: false,
  organizers: [],
  fees: [],
  expanded: true
});

// Events array (multiple events for new mode, single for edit)
const events = ref([createEmptyEvent()]);

// Uploaded images
const uploadedImages = ref([]);
const imageInput = ref(null);

// State
const errors = ref({});
const saving = ref(false);
const analyzing = ref(false);
const showConfirmDialog = ref(false);
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');
const availableOrganizers = ref([]);
const organizerSearchTimer = ref(null);

// Image Preview State
const showImagePreviewDialog = ref(false);
const previewImageSrc = ref('');

const isEdit = computed(() => !!route.params.id);

/**
 * Get image source URL for preview
 */
const getImageSrc = (img) => {
  if (img.file) {
    return URL.createObjectURL(img.file);
  }
  return `data:${img.mime_type};base64,${img.image_data}`;
};

/**
 * Handle image upload
 */
// Trigger image input click
const triggerImageInput = () => {
  imageInput.value?.click();
};

const handleImageUpload = (e) => {
  const files = Array.from(e.target.files);
  const remainingSlots = 5 - uploadedImages.value.length;
  const filesToAdd = files.slice(0, remainingSlots);

  filesToAdd.forEach(file => {
    uploadedImages.value.push({
      file: file,
      mime_type: file.type,
      original_filename: file.name
    });
  });

  // Reset input
  e.target.value = '';
};

/**
 * Remove uploaded image
 */
const removeImage = (index) => {
  uploadedImages.value.splice(index, 1);
};

/**
 * Open image preview dialog
 */
const openImagePreview = (img) => {
  previewImageSrc.value = getImageSrc(img);
  showImagePreviewDialog.value = true;
};

/**
 * Close image preview dialog
 */
const closeImagePreview = () => {
  showImagePreviewDialog.value = false;
  previewImageSrc.value = '';
};

/**
 * Analyze images with Gemini AI
 */
const analyzeImages = async () => {
  if (uploadedImages.value.length === 0) return;

  analyzing.value = true;

  try {
    const files = uploadedImages.value.map(img => img.file).filter(Boolean);
    if (files.length === 0) {
      errorMessage(t('masterEvents.errorNoFilesToAnalyze'));
      return;
    }

    const result = await analyzeEventFlyersMultiple(files);

    if (result.events && result.events.length > 0) {
      // Update events array based on analysis result
      const analyzedEvents = result.events.map(eventData => {
        // Find matching organizer by exact name match
        const organizers = [];
        if (eventData.organizer) {
          const matchedOrganizer = availableOrganizers.value.find(
            org => org.name === eventData.organizer
          );
          if (matchedOrganizer) {
            organizers.push({
              store_id: matchedOrganizer.id,
              is_primary: true,
              searchText: matchedOrganizer.name,
              showDropdown: false
            });
          }
        }

        return {
          id: null,
          title: eventData.title || '',
          description: eventData.description || '',
          startDate: eventData.start_date ? formatDateForInput(eventData.start_date) : '',
          endDate: eventData.end_date ? formatDateForInput(eventData.end_date) : '',
          capacity: eventData.guest_capacity ? parseInt(eventData.guest_capacity) : null,
          isOnline: false,
          organizers: organizers,
          fees: eventData.fee ? [{ fee_type: t('masterEvents.participationFee'), amount: parseInt(eventData.fee) || 0, description: '' }] : [],
          expanded: true
        };
      });

      events.value = analyzedEvents.slice(0, 5);

      // Update uploaded images with base64 data from response
      if (result.images && result.images.length > 0) {
        uploadedImages.value = result.images.map((img, i) => ({
          ...uploadedImages.value[i],
          image_data: img.image_data,
          mime_type: img.mime_type,
          original_filename: img.original_filename
        }));
      }

      successMessage(t('masterEvents.analyzeSuccess', { count: analyzedEvents.length }));
    }
  } catch (error) {
    errorMessage(error.message || t('masterEvents.errorAnalyze'));
  } finally {
    analyzing.value = false;
  }
};

/**
 * Format date string for input (YYYY/MM/DD -> YYYY-MM-DD)
 */
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.replace(/\//g, '-');
};

/**
 * Toggle event accordion
 */
const toggleEventAccordion = (index) => {
  events.value[index].expanded = !events.value[index].expanded;
};

/**
 * Add new event
 */
const addEvent = () => {
  if (events.value.length < 5) {
    events.value.push(createEmptyEvent());
  }
};

/**
 * Remove event
 */
const removeEvent = (index) => {
  if (events.value.length > 1) {
    events.value.splice(index, 1);
  }
};

/**
 * Add organizer to event
 */
const addOrganizerToEvent = (eventIndex) => {
  const newOrganizer = createEmptyOrganizer();
  // First organizer is primary by default
  if (events.value[eventIndex].organizers.length === 0) {
    newOrganizer.is_primary = true;
  }
  events.value[eventIndex].organizers.push(newOrganizer);
};

/**
 * Handle organizer search with debounce (for row-based UI)
 */
const handleOrganizerSearchInRow = (eventIndex, orgIndex) => {
  if (organizerSearchTimer.value) {
    clearTimeout(organizerSearchTimer.value);
  }

  organizerSearchTimer.value = setTimeout(async () => {
    const searchTerm = events.value[eventIndex].organizers[orgIndex].searchText;
    if (searchTerm.length >= 1) {
      try {
        availableOrganizers.value = await getAllEventOrganizers(searchTerm);
      } catch (error) {
        console.error('Error searching organizers:', error);
      }
    }
    events.value[eventIndex].organizers[orgIndex].showDropdown = true;
  }, 300);
};

/**
 * Get filtered organizers for a specific row
 */
const getFilteredOrganizersForRow = (eventIndex, orgIndex) => {
  const organizer = events.value[eventIndex].organizers[orgIndex];
  const searchTerm = (organizer.searchText || '').toLowerCase();
  const selectedIds = events.value[eventIndex].organizers
    .filter((_, i) => i !== orgIndex)
    .map(o => o.store_id)
    .filter(id => id);

  return availableOrganizers.value.filter(org =>
    !selectedIds.includes(org.id) &&
    org.name.toLowerCase().includes(searchTerm)
  );
};

/**
 * Select organizer in a specific row
 */
const selectOrganizerInRow = (eventIndex, orgIndex, org) => {
  events.value[eventIndex].organizers[orgIndex].store_id = org.id;
  events.value[eventIndex].organizers[orgIndex].searchText = org.name;
  events.value[eventIndex].organizers[orgIndex].showDropdown = false;
};

/**
 * Remove organizer from event
 */
const removeOrganizerFromEvent = (eventIndex, orgIndex) => {
  events.value[eventIndex].organizers.splice(orgIndex, 1);
};

/**
 * Get organizer error
 */
const getOrganizerError = (eventIndex, orgIndex) => {
  const key = isEdit.value
    ? `organizers.${orgIndex}.store_id`
    : `events.${eventIndex}.organizers.${orgIndex}.store_id`;
  return !!errors.value[key];
};

/**
 * Add fee to event
 */
const addFeeToEvent = (eventIndex) => {
  events.value[eventIndex].fees.push({
    fee_type: '',
    amount: 0,
    description: ''
  });
};

/**
 * Remove fee from event
 */
const removeFeeFromEvent = (eventIndex, feeIndex) => {
  events.value[eventIndex].fees.splice(feeIndex, 1);
};

/**
 * Get fee error for a specific field
 */
const getFeeError = (eventIndex, feeIndex, field) => {
  const key = isEdit.value
    ? `fees.${feeIndex}.${field}`
    : `events.${eventIndex}.fees.${feeIndex}.${field}`;
  const error = errors.value[key];
  return error ? error[0] : null;
};

/**
 * Get event error
 */
const getEventError = (eventIndex, field) => {
  const key = isEdit.value ? field : `events.${eventIndex}.${field}`;
  const error = errors.value[key];
  return error ? error[0] : null;
};

/**
 * Load event data and available organizers
 */
onMounted(async () => {
  try {
    // Load available organizers for dropdown
    availableOrganizers.value = await getAllEventOrganizers();

    if (isEdit.value) {
      const data = await getMasterEvent(route.params.id);
      // Transform organizers to include searchText (organizer name)
      const organizers = (data.organizers || []).map(org => {
        const organizerInfo = availableOrganizers.value.find(o => o.id === org.store_id);
        return {
          store_id: org.store_id,
          is_primary: org.is_primary,
          searchText: organizerInfo ? organizerInfo.name : '',
          showDropdown: false
        };
      });

      events.value = [{
        id: data.id,
        title: data.title,
        description: data.description || '',
        startDate: data.start_date,
        endDate: data.end_date,
        capacity: data.capacity,
        forecastedVisitorCount: data.forecasted_visitor_count,
        isOnline: data.is_online,
        organizers: organizers,
        fees: data.fees || [],
        expanded: true
      }];
    }

    // Close dropdown on outside click
    document.addEventListener('click', handleClickOutside);
  } catch (error) {
    errorMessage(error.message || t('masterEvents.errorFetchData'));
    router.push({ name: 'master.events.index' });
  }
});

/**
 * Handle click outside dropdown
 */
const handleClickOutside = (e) => {
  if (!e.target.closest('.organizer-select-container')) {
    events.value.forEach(event => {
      event.organizers.forEach(org => {
        org.showDropdown = false;
      });
    });
  }
};

/**
 * Validate all events
 */
const validate = () => {
  const newErrors = {};

  events.value.forEach((eventItem, eventIndex) => {
    const prefix = isEdit.value ? '' : `events.${eventIndex}.`;

    // Title validation
    if (!eventItem.title || eventItem.title.trim() === '') {
      newErrors[prefix + 'title'] = [t('masterEvents.validation.titleRequired')];
    }

    // Start date validation
    if (!eventItem.startDate) {
      newErrors[prefix + 'start_date'] = [t('masterEvents.validation.startDateRequired')];
    }

    // End date validation
    if (!eventItem.endDate) {
      newErrors[prefix + 'end_date'] = [t('masterEvents.validation.endDateRequired')];
    } else if (eventItem.startDate && eventItem.endDate < eventItem.startDate) {
      newErrors[prefix + 'end_date'] = [t('masterEvents.validation.endDateAfterStart')];
    }

    // Organizers validation - at least one with valid store_id required
    const validOrganizers = eventItem.organizers.filter(o => o.store_id);
    if (validOrganizers.length === 0) {
      newErrors[prefix + 'organizers'] = [t('masterEvents.validation.organizerRequired')];
    }

    // Forecasted visitor count validation - required if capacity is blank or <= 0
    const capacityIsEmpty = eventItem.capacity === null || eventItem.capacity === undefined || eventItem.capacity === '' || eventItem.capacity <= 0;
    if (capacityIsEmpty) {
      const forecastedIsEmpty = eventItem.forecastedVisitorCount === null || eventItem.forecastedVisitorCount === undefined || eventItem.forecastedVisitorCount === '' || eventItem.forecastedVisitorCount < 1;
      if (forecastedIsEmpty) {
        newErrors[prefix + 'forecasted_visitor_count'] = [t('masterEvents.validation.forecastedVisitorCountRequired')];
      }
    }

    // Fees validation - if fee row exists, fee_type and amount are required
    eventItem.fees.forEach((fee, feeIndex) => {
      const feePrefix = prefix + `fees.${feeIndex}.`;
      if (!fee.fee_type || fee.fee_type.trim() === '') {
        newErrors[feePrefix + 'fee_type'] = [t('masterEvents.validation.feeTypeRequired')];
      }
      if (fee.amount === null || fee.amount === '' || fee.amount === undefined) {
        newErrors[feePrefix + 'amount'] = [t('masterEvents.validation.feeAmountRequired')];
      }
    });
  });

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

/**
 * Save events
 */
const save = async () => {
  if (!validate()) {
    return;
  }

  saving.value = true;
  errors.value = {};

  try {
    if (isEdit.value) {
      // Single event update
      const eventItem = events.value[0];
      const eventData = {
        title: eventItem.title,
        description: eventItem.description || null,
        start_date: eventItem.startDate,
        end_date: eventItem.endDate,
        capacity: eventItem.capacity,
        forecasted_visitor_count: eventItem.forecastedVisitorCount || null,
        is_online: eventItem.isOnline,
        organizers: eventItem.organizers.filter(o => o.store_id),
        fees: eventItem.fees.filter(f => f.fee_type && f.amount !== null && f.amount !== '')
      };

      await updateMasterEvent(eventItem.id, eventData);
      successMessage(t('masterEvents.successUpdate'));
    } else {
      // Bulk event creation
      const eventsData = events.value.map(eventItem => ({
        title: eventItem.title,
        description: eventItem.description || null,
        start_date: eventItem.startDate,
        end_date: eventItem.endDate,
        capacity: eventItem.capacity || 0,
        forecasted_visitor_count: eventItem.forecastedVisitorCount || null,
        is_online: eventItem.isOnline,
        organizers: eventItem.organizers.filter(o => o.store_id),
        fees: eventItem.fees.filter(f => f.fee_type && f.amount !== null && f.amount !== '')
      }));

      const imagesData = uploadedImages.value
        .filter(img => img.image_data)
        .map(img => ({
          image_data: img.image_data,
          mime_type: img.mime_type,
          original_filename: img.original_filename
        }));

      await createMasterEventsBulk({
        events: eventsData,
        images: imagesData
      });

      successMessage(t('masterEvents.successCreateBulk', { count: events.value.length }));
    }

    router.push({ name: 'master.events.index' });
  } catch (error) {
    if (error.status === 422 && error.errors) {
      errors.value = error.errors;
    } else {
      errorMessage(error.message || t('masterEvents.errorSave'));
    }
  } finally {
    saving.value = false;
  }
};

/**
 * Cancel and go back
 */
const cancel = () => {
  router.push({ name: 'master.events.index' });
};

/**
 * Show delete confirmation dialog
 */
const confirmDelete = async () => {
  try {
    const eventItem = events.value[0];
    const result = await checkMasterEventDeletable(eventItem.id);

    if (!result.deletable) {
      errorDialogMessage.value = result.message || t('masterEvents.errorDeleteInUse');
      showErrorDialog.value = true;
    } else {
      showConfirmDialog.value = true;
    }
  } catch (error) {
    errorMessage(error.message || t('masterEvents.errorDelete'));
  }
};

/**
 * Handle delete confirmation
 */
const handleDeleteConfirm = async () => {
  try {
    const eventItem = events.value[0];
    await deleteMasterEvent(eventItem.id);
    successMessage(t('masterEvents.successDelete'));
    router.push({ name: 'master.events.index' });
  } catch (error) {
    errorMessage(error.message || t('masterEvents.errorDelete'));
  }
};
</script>

<style scoped>
.container-fluid {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  width: 100%;
  max-width: 800px;
  margin-bottom: 1rem;
}

.page-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin-bottom: 0.5rem;
}

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px v-bind('colors.shadowColor');
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
}

.text-danger {
  font-weight: bold;
}

/* Image Upload Section */
.image-upload-section {
  border-bottom: 1px solid v-bind('colors.gray[200]');
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
}

.images-preview-container {
  margin-bottom: 1rem;
}

.images-preview-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0;
  scroll-snap-type: x mandatory;
}

.images-preview-scroll::-webkit-scrollbar {
  height: 6px;
}

.images-preview-scroll::-webkit-scrollbar-track {
  background: v-bind('colors.gray[100]');
  border-radius: 3px;
}

.images-preview-scroll::-webkit-scrollbar-thumb {
  background: v-bind('colors.gray[400]');
  border-radius: 3px;
}

.image-preview-item {
  position: relative;
  flex-shrink: 0;
  scroll-snap-align: start;
}

.preview-image {
  width: 200px;
  height: 280px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid v-bind('colors.gray[200]');
}

.btn-remove-image {
  position: absolute;
  top: 8px;
  right: 8px;
  background: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-remove-image i {
  color: v-bind('colors.gray[600]');
  font-size: 18px;
}

.btn-remove-image:hover i {
  color: #dc3545;
}

.add-image-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-add-image {
  color: v-bind('colors.btnBgColorPrimary');
  background: none;
  border: none;
  padding: 8px 0;
  font-size: 14px;
  cursor: pointer;
}

.btn-add-image:hover {
  text-decoration: underline;
}

.btn-add-image:disabled {
  color: v-bind('colors.disabled');
  cursor: not-allowed;
}

.image-count-hint {
  color: v-bind('colors.gray[500]');
  font-size: 12px;
}

.analyze-section {
  text-align: center;
}

.btn-analyze {
  background-color: v-bind('colors.btnBgColorPrimary');
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
}

.btn-analyze:hover:not(:disabled) {
  background-color: v-bind('colors.chartBlue[800]');
}

.btn-analyze:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Event Accordion */
.event-accordion {
  border: 1px solid v-bind('colors.gray[200]');
  border-radius: 8px;
  overflow: hidden;
}

.event-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  border-bottom: 1px solid v-bind('colors.gray[200]');
  transition: background-color 0.2s;
}

.event-accordion-header:hover {
  background-color: v-bind('colors.gray[50]');
}

.event-title-text {
  font-weight: 600;
  font-size: 16px;
  color: v-bind('colors.textPrimary');
}

.accordion-icon {
  color: v-bind('colors.btnBgColorPrimary');
  font-size: 18px;
}

.event-accordion-body {
  padding: 1.5rem;
}

/* Section Header */
.section-header {
  padding: 12px 0;
  border-bottom: 1px solid v-bind('colors.gray[200]');
  margin-bottom: 12px;
}

.section-header .form-label {
  font-weight: 600;
  color: v-bind('colors.textPrimary');
}

.section-content {
  padding-left: 8px;
}

/* Organizer Row */
.organizer-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.organizer-select-container {
  position: relative;
  flex: 1;
}

.organizer-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid v-bind('colors.gray[200]');
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.organizer-dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
}

.organizer-dropdown-item:hover {
  background-color: v-bind('colors.gray[100]');
}

.organizer-primary-switch {
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 8px;
}

.organizer-primary-switch .form-check-input {
  margin-top: 0;
}

.btn-remove-organizer,
.btn-remove-fee {
  flex-shrink: 0;
  color: v-bind('colors.btnBgColorPrimary');
  padding: 4px 8px;
  text-decoration: none;
}

.btn-remove-organizer:hover,
.btn-remove-fee:hover {
  color: v-bind('colors.chartBlue[800]');
}

.btn-remove-organizer i,
.btn-remove-fee i {
  font-size: 18px;
}

/* Add row button */
.btn-add-row {
  padding: 6px 16px;
  font-size: 14px;
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
}

.btn-add-row:hover {
  background-color: v-bind('colors.chartBlue[800]');
  border-color: v-bind('colors.chartBlue[800]');
}

/* Toggle Switch Colors */
.form-switch .form-check-input {
  background-color: v-bind('colors.toggle.inactive');
  border-color: v-bind('colors.toggle.inactive');
}

.form-switch .form-check-input:checked {
  background-color: v-bind('colors.toggle.active');
  border-color: v-bind('colors.toggle.active');
}

.form-switch .form-check-input:focus {
  box-shadow: 0 0 0 0.25rem rgba(48, 111, 201, 0.25);
  border-color: v-bind('colors.toggle.active');
}

/* Date Range */
.date-range-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-input {
  flex: 1;
}

.date-separator {
  color: v-bind('colors.gray[500]');
  font-size: 18px;
}

/* Fees */
.fee-row-wrapper {
  display: flex;
  flex-direction: column;
}

.fee-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.fee-errors {
  padding-left: 8px;
  margin-top: 4px;
}

.fee-row-inputs {
  display: flex;
  gap: 8px;
  flex: 1;
}

.fee-type-input {
  flex: 1;
  min-width: 120px;
}

.fee-amount-group {
  width: 150px;
  flex-shrink: 0;
}

.fee-description-input {
  flex: 1;
  min-width: 150px;
}

/* Capacity */
.capacity-input {
  max-width: 200px;
}

/* Delete Event */
.delete-event-section {
  text-align: center;
  border-top: 1px solid v-bind('colors.gray[200]');
  padding-top: 1rem;
  margin-top: 1rem;
}

.btn-delete-event {
  color: v-bind('colors.btnBgColorPrimary');
  text-decoration: none;
}

.btn-delete-event:hover {
  text-decoration: underline;
}

/* Add Event Section */
.add-event-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid v-bind('colors.gray[200]');
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
}

.add-event-section:hover {
  background-color: v-bind('colors.gray[50]');
}

.add-event-text {
  font-weight: 500;
  color: v-bind('colors.textPrimary');
}

.add-event-icon {
  color: v-bind('colors.btnBgColorPrimary');
  font-size: 20px;
}

/* Action buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 2rem;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  background-color: v-bind('colors.btnBgColorPrimary');
  border: none;
  border-radius: 6px;
}

.btn-submit:hover:not(:disabled) {
  background-color: v-bind('colors.chartBlue[800]');
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.disabled');
  border-radius: 6px;
}

.btn-cancel:hover {
  background-color: v-bind('colors.gray[50]');
  color: v-bind('colors.btnBgColorPrimary');
}

.btn-delete {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  color: v-bind('colors.btnBgColorPrimary');
  text-decoration: none;
  border: none;
  background: none;
  text-align: center;
}

.btn-delete:hover {
  text-decoration: underline;
  color: v-bind('colors.chartBlue[800]');
}

.btn-delete i {
  margin-right: 4px;
}

/* Image Preview - Clickable */
.preview-image.clickable {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.preview-image.clickable:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Image Preview Dialog */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.image-preview-dialog {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.btn-close-preview {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.btn-close-preview:hover {
  opacity: 1;
}

.preview-large-image {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
